import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */

/* Sultanea: doble aviso acústico opcional, a volumen muy bajo.
   Los navegadores móviles solo permiten audio después de una interacción. */
let suBuySoundContext = null;
function suUnlockBuySound() {
  const AudioClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioClass) return;
  try {
    if (!suBuySoundContext) suBuySoundContext = new AudioClass();
    if (suBuySoundContext.state === 'suspended') {
      suBuySoundContext.resume().catch(() => {});
    }
    if (suBuySoundContext.state === 'running') {
      window.removeEventListener('pointerdown', suUnlockBuySound, true);
      window.removeEventListener('touchend', suUnlockBuySound, true);
      window.removeEventListener('keydown', suUnlockBuySound, true);
    }
  } catch (_) {
    // El navegador puede bloquear el sonido; la animación visual sigue funcionando.
  }
}
window.addEventListener('pointerdown', suUnlockBuySound, { passive: true, capture: true });
window.addEventListener('touchend', suUnlockBuySound, { passive: true, capture: true });
window.addEventListener('keydown', suUnlockBuySound, { capture: true });

function suPlayBuyHeartbeat() {
  if (!suBuySoundContext || suBuySoundContext.state !== 'running' || document.hidden) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  try {
    const start = suBuySoundContext.currentTime + 0.01;
    [0, 0.22].forEach((offset) => {
      const at = start + offset;
      const tone = suBuySoundContext.createOscillator();
      const volume = suBuySoundContext.createGain();
      tone.type = 'sine';
      tone.frequency.setValueAtTime(215, at);
      tone.frequency.exponentialRampToValueAtTime(125, at + 0.1);
      volume.gain.setValueAtTime(0.0001, at);
      volume.gain.exponentialRampToValueAtTime(0.012, at + 0.015);
      volume.gain.exponentialRampToValueAtTime(0.0001, at + 0.115);
      tone.connect(volume).connect(suBuySoundContext.destination);
      tone.start(at);
      tone.stop(at + 0.12);
      tone.addEventListener('ended', () => {
        tone.disconnect();
        volume.disconnect();
      }, { once: true });
    });
  } catch (_) {
    // Sin sonido si no se admite Web Audio.
  }
}

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {number | null} */
  #attentionTimer = null;

  /** @type {number | null} */
  #attentionInterval = null;

  /** @type {number | null} */
  #attentionCleanupTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // Pausar los latidos cuando se abandona la pestaña y reanudarlos al volver.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#stopAttention();
      else if (this.#isStuck) this.#startAttention();
    }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    this.#stopAttention();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hideStickyBar();
      }
    });

    // La compra flotante continúa visible incluso al llegar al pie de página.
    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#startAttention();
  }

  #startAttention() {
    if (!this.#isStuck || document.hidden || !this.isConnected) return;
    if (this.#attentionTimer !== null || this.#attentionInterval !== null) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Primera pulsación al poco de entrar; luego doble "bum-bum" cada 2 segundos.
    this.#attentionTimer = window.setTimeout(() => {
      this.#attentionTimer = null;
      if (!this.#isStuck || document.hidden || !this.isConnected) return;
      this.#pulseAttention();
      this.#attentionInterval = window.setInterval(() => {
        if (!this.#isStuck || document.hidden || !this.isConnected) {
          this.#stopAttention();
          return;
        }
        this.#pulseAttention();
      }, 2000);
    }, 370);
  }

  #pulseAttention() {
    if (!this.#isStuck || document.hidden) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#stopAttention();
      return;
    }
    const { stickyBar } = this.refs;
    if (!stickyBar) return;
    if (this.#attentionCleanupTimer !== null) clearTimeout(this.#attentionCleanupTimer);

    // Reiniciar el CSS animation cada 2 s (repetir clase sola no lo reinicia).
    stickyBar.classList.remove('su-buy-attention');
    void stickyBar.offsetWidth;
    stickyBar.classList.add('su-buy-attention');

    if (typeof navigator.vibrate === 'function') {
      try { navigator.vibrate([25, 110, 25]); } catch (_) {}
    }
    suPlayBuyHeartbeat(); // Solo suena si el navegador permite AudioContext.
    this.#attentionCleanupTimer = window.setTimeout(() => {
      stickyBar.classList.remove('su-buy-attention');
      this.#attentionCleanupTimer = null;
    }, 1300);
  }

  #stopAttention() {
    if (this.#attentionTimer !== null) {
      clearTimeout(this.#attentionTimer);
      this.#attentionTimer = null;
    }
    if (this.#attentionInterval !== null) {
      clearInterval(this.#attentionInterval);
      this.#attentionInterval = null;
    }
    if (this.#attentionCleanupTimer !== null) {
      clearTimeout(this.#attentionCleanupTimer);
      this.#attentionCleanupTimer = null;
    }
    this.refs.stickyBar?.classList.remove('su-buy-attention');
    if (typeof navigator.vibrate === 'function') {
      try { navigator.vibrate(0); } catch (_) {}
    }
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#stopAttention();
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
